import React from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Typography,
    List,
    ListItem,
    ListItemText,
    Divider,
    useTheme,
} from "@mui/material";

const ModuleHelpDialog = ({ open, onClose, data }) => {
    const theme = useTheme();

    if (!data) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor:
                        theme.palette.mode === "dark"
                            ? theme.palette.blueAccent?.[500] || theme.palette.primary.main
                            : theme.palette.background.paper,
                    color:
                        theme.palette.mode === "dark"
                            ? theme.palette.getContrastText(
                                theme.palette.blueAccent?.[500] || theme.palette.primary.main
                            )
                            : theme.palette.text.primary,
                },
            }}
        >

            <DialogTitle sx={{ fontWeight: "bold" }}>{data.title} - Help</DialogTitle>

            <DialogContent dividers>
                {/* Description */}
                {data.description && (
                    <Typography variant="h6" sx={{ mb: 1.5, whiteSpace: "pre-line" }}>
                        {data.description}
                    </Typography>
                )}

                {/* Workflow Steps */}
                {data.steps && data.steps.length > 0 && (
                    <>
                        <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: "bold", mb: 0.5 }}
                        >
                            Workflow Steps:
                        </Typography>
                        <List dense>
                            {data.steps.map((step, i) => (
                                <ListItem key={i} alignItems="flex-start">
                                    <ListItemText
                                        primary={<b>{step.title}</b>}
                                        secondary={step.details}
                                    />
                                </ListItem>
                            ))}
                        </List>
                        <Divider sx={{ my: 2 }} />
                    </>
                )}

                {/* Fields */}
                {data.fields && data.fields.length > 0 && (
                    <>
                        <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: "bold", mb: 0.5 }}
                        >
                            Fields:
                        </Typography>
                        <List dense>
                            {data.fields.map((f, i) => (
                                <ListItem key={i} alignItems="flex-start">
                                    <ListItemText
                                        primary={
                                            <>
                                                {f.field}{" "}
                                                {f.required && <span style={{ color: "red" }}>*</span>}
                                            </>
                                        }
                                        secondary={f.hint}
                                    />
                                </ListItem>
                            ))}
                        </List>
                        <Divider sx={{ my: 2 }} />
                    </>
                )}

                {/* Buttons */}
                {data.buttons && data.buttons.length > 0 && (
                    <>
                        <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: "bold", mb: 0.5 }}
                        >
                            Button Actions:
                        </Typography>
                        <List dense>
                            {data.buttons.map((b, i) => (
                                <ListItem key={i}>
                                    <ListItemText primary={b.name} secondary={b.hint} />
                                </ListItem>
                            ))}
                        </List>
                    </>
                )}

                {/* Example Record Set */}
                {data.exampleRecordSet && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                            Example Record Set:
                        </Typography>

                        {/* Display top-level fields */}
                        <List dense>
                            {Object.entries(data.exampleRecordSet)
                                .filter(([key]) => key !== "Locations")
                                .map(([key, value], i) => (
                                    <ListItem key={i}>
                                        <ListItemText
                                            primary={<b>{key}</b>}
                                            secondary={String(value)}
                                        />
                                    </ListItem>
                                ))}
                        </List>

                        {/* Display Locations if present */}
                        {Array.isArray(data.exampleRecordSet.Locations) && (
                            <>
                                <Typography variant="subtitle2" sx={{ fontWeight: "bold", mt: 2 }}>
                                    Bin Locations:
                                </Typography>
                                <List dense>
                                    {data.exampleRecordSet.Locations.map((loc, i) => (
                                        <ListItem key={i} alignItems="flex-start">
                                            <ListItemText
                                                primary={<b>{loc.LocationCode}</b>}
                                                secondary={
                                                    <>
                                                        Width: {loc.Width} cm, Length: {loc.Length} cm, Height: {loc.Height} cm<br />
                                                        Max Weight: {loc.MaxWeight} kg, Volume: {loc.MaxVolume}, Item Count: {loc.MaxItemCount}<br />
                                                        Mixed Items: {loc.AllowMixedItems ? "Yes" : "No"}, Mixed Batches: {loc.AllowMixedBatches ? "Yes" : "No"}
                                                    </>
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </>
                        )}
                    </>
                )}



            </DialogContent>
        </Dialog>
    );
};

export default ModuleHelpDialog;
